import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { playMessageSound, playMentionSound } from "@/lib/sound";
import { toast } from "sonner";
import {
  SquaresFour, NotePencil, Wrench, Users, Package, UserGear,
  SignOut, Sun, Moon, Cpu, ChatCircleDots, List, DeviceMobile, ArrowSquareOut, Bell, ChartBar, CalendarBlank, Headset, CalendarCheck, Check, Eye, EyeSlash,
} from "@phosphor-icons/react";

const NAMED_COLORS = [
  [/marco/i, { active: "bg-green-500 text-white ring-2 ring-green-300", dot: "bg-green-500", hsl: "142 71% 45%" }],
  [/michelangelo/i, { active: "bg-blue-500 text-white ring-2 ring-blue-300", dot: "bg-blue-500", hsl: "217 91% 60%" }],
  [/emiliano/i, { active: "bg-orange-500 text-white ring-2 ring-orange-300", dot: "bg-orange-500", hsl: "25 95% 53%" }],
];
const FALLBACK_COLORS = [
  { active: "bg-violet-500 text-white ring-2 ring-violet-300", dot: "bg-violet-500", hsl: "262 83% 58%" },
  { active: "bg-rose-500 text-white ring-2 ring-rose-300", dot: "bg-rose-500", hsl: "347 77% 50%" },
  { active: "bg-cyan-500 text-white ring-2 ring-cyan-300", dot: "bg-cyan-500", hsl: "189 94% 43%" },
  { active: "bg-amber-500 text-white ring-2 ring-amber-300", dot: "bg-amber-500", hsl: "38 92% 50%" },
];
const opColor = (name, i) => (NAMED_COLORS.find(([re]) => re.test(name)) || [null, FALLBACK_COLORS[i % FALLBACK_COLORS.length]])[1];

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard", end: true },
  { to: "/prima-nota", label: "Prima Nota", icon: NotePencil, testid: "nav-prima-nota" },
  { to: "/riparazioni", label: "Riparazioni", icon: Wrench, testid: "nav-riparazioni" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { to: "/ordini", label: "Ordini Ricambi", icon: Package, testid: "nav-ordini" },
  { to: "/prenotazioni", label: "Richieste", icon: CalendarBlank, testid: "nav-prenotazioni" },
  { to: "/messaggi", label: "Messaggi", icon: ChatCircleDots, testid: "nav-messaggi" },
  { to: "/statistiche", label: "Statistiche", icon: ChartBar, testid: "nav-statistiche", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [unread, setUnread] = useState(0);
  const [mentions, setMentions] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const prevCounts = useRef({ count: 0, mentions: 0 });
  const firstCheck = useRef(true);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!firstCheck.current) {
          if (data.mentions > prevCounts.current.mentions) playMentionSound();
          else if (data.count > prevCounts.current.count) playMessageSound();
        }
        firstCheck.current = false;
        prevCounts.current = { count: data.count, mentions: data.mentions };
        setUnread(data.count);
        setMentions(data.mentions);
      } catch { /* ignore */ }
    };
    check();
    const iv = setInterval(check, 8000);
    window.addEventListener("chat-seen", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("chat-seen", check);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const [operators, setOperators] = useState([]);
  const [activeOp, setActiveOp] = useState(() => localStorage.getItem("active_operator") || "");

  useEffect(() => {
    api.get("/users/technicians").then((r) => {
      const list = [...(r.data || [])].filter((t) => !t.generic);
      if (user && !user.generic && !list.some((t) => t.name === user.name)) list.unshift({ id: user.id, name: user.name, role: user.role });
      setOperators(list);
      if (!localStorage.getItem("active_operator") && user && !user.generic) {
        localStorage.setItem("active_operator", user.name);
        setActiveOp(user.name);
      }
    }).catch(() => {});
  }, [user]);

  const activeOpRole = (operators.find((o) => o.name === activeOp)?.role) || (user?.generic ? null : user?.role);

  useEffect(() => {
    const idx = operators.findIndex((o) => o.name === activeOp);
    const root = document.documentElement;
    if (idx >= 0) {
      const c = opColor(activeOp, idx);
      root.style.setProperty("--primary", c.hsl);
      root.style.setProperty("--ring", c.hsl);
    } else {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    }
  }, [activeOp, operators]);

  const [pwOp, setPwOp] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const applyOp = (name, token = null) => {
    localStorage.setItem("active_operator", name);
    if (token) localStorage.setItem("operator_token", token);
    else localStorage.removeItem("operator_token");
    setActiveOp(name);
    window.dispatchEvent(new Event("operator-changed"));
  };

  const switchOp = (op) => {
    if (op.role === "admin") {
      setPwOp(op);
      setPwValue("");
      setPwShow(false);
      setPwError("");
      return;
    }
    applyOp(op.name);
  };

  const confirmOpPassword = async () => {
    if (!pwValue || pwBusy) return;
    setPwBusy(true);
    setPwError("");
    try {
      const r = await api.post("/auth/verify-password", { name: pwOp.name, password: pwValue });
      applyOp(pwOp.name, r.data.operator_token || null);
      setPwOp(null);
    } catch {
      setPwError("Password errata");
    } finally {
      setPwBusy(false);
    }
  };

  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const prevNotifUnread = useRef(0);
  const firstNotifCheck = useRef(true);

  useEffect(() => {
    const check = async () => {
      try {
        const op = localStorage.getItem("active_operator");
        const { data } = await api.get("/notifications", { params: op ? { operator: op } : {} });
        if (!firstNotifCheck.current && data.unread > prevNotifUnread.current) playMentionSound();
        firstNotifCheck.current = false;
        prevNotifUnread.current = data.unread;
        setNotifs(data.items);
        setNotifUnread(data.unread);
      } catch { /* ignore */ }
    };
    check();
    const iv = setInterval(check, 8000);
    const onOpChange = () => {
      firstNotifCheck.current = true;
      prevNotifUnread.current = 0;
      check();
    };
    window.addEventListener("operator-changed", onOpChange);
    return () => {
      clearInterval(iv);
      window.removeEventListener("operator-changed", onOpChange);
    };
  }, []);

  useEffect(() => {
    const open = () => {
      if (window.innerWidth < 1024) setMenuOpen(true);
      setNotifOpen(true);
    };
    window.addEventListener("open-notifications", open);
    return () => window.removeEventListener("open-notifications", open);
  }, []);

  const opParams = () => {
    const op = localStorage.getItem("active_operator");
    return op ? { operator: op } : {};
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all", null, { params: opParams() });
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotifUnread(0);
      prevNotifUnread.current = 0;
    } catch { /* ignore */ }
  };

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`, null, { params: opParams() }); } catch { /* ignore */ }
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setNotifUnread((u) => Math.max(0, u - 1));
      prevNotifUnread.current = Math.max(0, prevNotifUnread.current - 1);
    }
    setNotifOpen(false);
    if (n.repair_id) navigate("/riparazioni");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {menuOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="sidebar-backdrop" />
      )}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-20 bg-card border-b border-border px-4 py-3 flex items-center gap-3" data-testid="mobile-topbar">
        <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button"
          className="p-2 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors duration-150">
          <List size={20} />
        </button>
        <p className="font-heading font-extrabold tracking-tight">TechGest</p>
      </div>
      <aside className={`w-60 shrink-0 border-r border-border bg-card flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div onClick={() => { navigate("/"); setMenuOpen(false); }} data-testid="logo-home-button"
          className="px-4 py-3 border-b border-border flex items-center gap-2.5 cursor-pointer hover:bg-accent/50 transition-colors duration-150">
          <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-md">
            <Cpu size={16} weight="duotone" className="text-primary-foreground" />
          </div>
          <div>
            <p className="font-heading font-extrabold tracking-tight leading-none text-sm" data-testid="app-title">TechGest</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">Gestionale Officina</p>
          </div>
        </div>
        {operators.length > 0 && (
          <div className="px-3 py-2.5 border-b border-border" data-testid="operator-switcher">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground px-1 mb-1.5">Operatore attivo</p>
            <div className="grid grid-cols-2 gap-1.5">
              {operators.map((op, i) => {
                const c = opColor(op.name, i);
                const isActive = activeOp === op.name;
                const firstName = op.name.trim().split(" ")[0];
                const label = firstName.charAt(0).toUpperCase() + firstName.slice(1);
                return (
                  <button key={op.id || op.name} onClick={() => switchOp(op)} data-testid={`operator-switch-${op.name.toLowerCase().replace(/\s+/g, "-")}`}
                    title={op.name}
                    className={`flex items-center gap-1.5 px-2 py-2 rounded-md text-xs font-bold transition-colors duration-150 ${isActive ? c.active : "border border-border text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${c.dot} ${isActive ? "" : "opacity-40"}`} />
                    <span className="truncate">{label}</span>
                    {isActive && <Check size={12} weight="bold" className="ml-auto shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.filter((item) => !item.adminOnly || activeOpRole === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMenuOpen(false)}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <item.icon size={19} weight="duotone" />
              {item.label}
              {item.to === "/messaggi" && (unread > 0 || mentions > 0) && (
                <span data-testid="chat-badge"
                  className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold inline-flex items-center justify-center ${
                    mentions > 0 ? "bg-amber-500 blink-red" : "bg-red-500"}`}>
                  {mentions > 0 ? `@${mentions}` : unread}
                </span>
              )}
            </NavLink>
          ))}
          {activeOpRole === "admin" && (
            <NavLink
              to="/dipendenti"
              data-testid="nav-dipendenti"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <UserGear size={19} weight="duotone" />
              Dipendenti
            </NavLink>
          )}
          <div className="flex items-center gap-2 px-3 pt-2" data-testid="nav-external-links">
            <a href="https://smart.memexsnc.it" target="_blank" rel="noopener noreferrer" title="Smartphone"
              onClick={() => setMenuOpen(false)} data-testid="nav-smartphone"
              className="flex-1 flex items-center justify-center py-2.5 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150">
              <DeviceMobile size={18} weight="duotone" />
            </a>
            <a href="https://assistenze.memexsnc.it" target="_blank" rel="noopener noreferrer" title="Assistenza"
              onClick={() => setMenuOpen(false)} data-testid="nav-assistenza"
              className="flex-1 flex items-center justify-center py-2.5 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150">
              <Headset size={18} weight="duotone" />
            </a>
            <a href="https://calendar.memexsnc.it" target="_blank" rel="noopener noreferrer" title="Appuntamenti"
              onClick={() => setMenuOpen(false)} data-testid="nav-appuntamenti"
              className="flex-1 flex items-center justify-center py-2.5 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150">
              <CalendarCheck size={18} weight="duotone" />
            </a>
          </div>
        </nav>
        <div className="border-t border-border p-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)} data-testid="notifications-bell"
                className="relative h-full px-3 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <Bell size={15} />
                {notifUnread > 0 && (
                  <span data-testid="notifications-badge"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold inline-flex items-center justify-center blink-red">
                    {notifUnread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute bottom-full mb-2 left-0 w-80 max-h-96 overflow-y-auto border border-border rounded-md bg-card shadow-xl z-50" data-testid="notifications-panel">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notifiche</p>
                    {notifUnread > 0 && (
                      <button onClick={markAllRead} data-testid="notifications-read-all"
                        className="text-[10px] font-semibold text-primary hover:opacity-80 transition-opacity duration-150">
                        Segna tutte lette
                      </button>
                    )}
                  </div>
                  {notifs.length === 0 && (
                    <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="notifications-empty">Nessuna notifica</p>
                  )}
                  {notifs.map((n) => (
                    <button key={n.id} onClick={() => openNotif(n)} data-testid={`notification-${n.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-accent/60 transition-colors duration-150 ${n.read ? "opacity-55" : ""}`}>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                        {n.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle-button"
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150"
            >
              {dark ? <Sun size={15} /> : <Moon size={15} />}
              {dark ? "Chiaro" : "Scuro"}
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-accent transition-colors duration-150"
            >
              <SignOut size={15} />
              Esci
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 ml-0 lg:ml-60 pt-14 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
      {pwOp && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="operator-password-dialog" onClick={() => setPwOp(null)}>
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-lg tracking-tight">Accesso {pwOp.name}</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Inserisci la password per lavorare come {pwOp.name}.</p>
            <form onSubmit={(e) => { e.preventDefault(); confirmOpPassword(); }}>
              <div className="relative">
                <input
                  type={pwShow ? "text" : "password"}
                  value={pwValue}
                  onChange={(e) => setPwValue(e.target.value)}
                  autoFocus
                  data-testid="operator-password-input"
                  className="w-full px-3.5 py-2.5 pr-10 rounded-md border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors duration-150"
                  placeholder="Password"
                />
                <button type="button" onClick={() => setPwShow((v) => !v)} data-testid="operator-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150">
                  {pwShow ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {pwError && <p className="text-red-500 text-xs font-semibold mt-2" data-testid="operator-password-error">{pwError}</p>}
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => setPwOp(null)} data-testid="operator-password-cancel"
                  className="flex-1 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150">
                  Annulla
                </button>
                <button type="submit" disabled={pwBusy || !pwValue} data-testid="operator-password-confirm"
                  className="flex-1 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                  {pwBusy ? "Verifica..." : "Conferma"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

